/**
 * AIButtonTrigger - Trigger Button for AI Suggestions Panel
 *
 * A CVA-based button component that displays different states:
 * - idle: Default sparkle icon
 * - loading: Spinning loader
 * - hasSuggestions: Badge with count
 * - error: Error icon
 *
 * @module features/ai-button/components/AIButtonTrigger
 * @see SPEC-019 Task 1.1
 * @see Requirements 1.1, 1.4, 1.5, 1.6, 8.1, 8.2
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/cn';

// ============================================================================
// CVA Variants
// ============================================================================

/**
 * AIButtonTrigger variants using CVA (Class Variance Authority)
 * Defines state and size variations
 */
export const aiButtonTriggerVariants = cva(
  // Base styles (always applied)
  [
    'inline-flex items-center justify-center',
    'rounded-full border-2 border-linkode-text',
    'bg-linkode-bg text-linkode-text',
    'transition-all duration-200',
    'hover:bg-linkode-bg-light hover:shadow-md',
    'focus:outline-none focus:ring-2 focus:ring-linkode-primary focus:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'relative',
    // Touch target support (min 44px for mobile accessibility)
    'touch-target min-h-11 min-w-11',
    // Reduced motion support
    'motion-reduce:transition-none',
  ],
  {
    variants: {
      state: {
        idle: [],
        loading: ['cursor-wait'],
        hasSuggestions: ['bg-linkode-primary'],
        error: ['border-red-500 text-red-500'],
      },
      size: {
        sm: ['h-8', 'w-8'],
        md: ['h-10', 'w-10'],
        lg: ['h-12', 'w-12'],
      },
    },
    defaultVariants: {
      state: 'idle',
      size: 'md',
    },
  }
);

/** Exported type for AIButtonTrigger variants */
export type AIButtonTriggerVariants = VariantProps<typeof aiButtonTriggerVariants>;

// ============================================================================
// Types
// ============================================================================

export interface AIButtonTriggerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>, AIButtonTriggerVariants {
  /** Number of suggestions to display in badge */
  suggestionCount?: number;
  /** Whether the panel is expanded */
  isExpanded?: boolean;
  /** Accessible label for the button */
  'aria-label': string;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Loading spinner icon */
const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/** Sparkle icon for idle state */
const SparkleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="sparkle-icon"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z" />
    <path d="M18 14L18.545 16.455L21 17L18.545 17.545L18 20L17.455 17.545L15 17L17.455 16.455L18 14Z" />
    <path d="M6 14L6.545 16.455L9 17L6.545 17.545L6 20L5.455 17.545L3 17L5.455 16.455L6 14Z" />
  </svg>
);

/** Error icon */
const ErrorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="error-icon"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

/** Suggestion count badge */
const SuggestionBadge: React.FC<{ count: number }> = ({ count }) => (
  <span
    data-testid="suggestion-badge"
    className={cn(
      'absolute -top-1 -right-1',
      'min-w-[1.25rem] h-5 px-1',
      'flex items-center justify-center',
      'rounded-full bg-red-500 text-white',
      'text-xs font-bold'
    )}
  >
    {count}
  </span>
);

// ============================================================================
// Component
// ============================================================================

/**
 * AIButtonTrigger Component
 *
 * A button that triggers the AI suggestions panel with visual state indicators.
 *
 * @example
 * ```tsx
 * <AIButtonTrigger
 *   state="idle"
 *   size="md"
 *   aria-label="AI Suggestions"
 *   onClick={() => setIsOpen(true)}
 * />
 * ```
 */
export const AIButtonTrigger: React.FC<AIButtonTriggerProps> = ({
  state = 'idle',
  size = 'md',
  suggestionCount = 0,
  isExpanded = false,
  className,
  disabled,
  'aria-label': ariaLabel,
  ...props
}) => {
  const isLoading = state === 'loading';
  const isDisabled = disabled || isLoading;

  // Icon size based on button size
  const iconSizeClass = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  }[size ?? 'md'];

  return (
    <button
      type="button"
      className={cn(aiButtonTriggerVariants({ state, size }), className)}
      disabled={isDisabled}
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={isExpanded}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {/* Loading state */}
      {state === 'loading' && <LoadingSpinner className={iconSizeClass} />}

      {/* Idle state */}
      {state === 'idle' && <SparkleIcon className={iconSizeClass} />}

      {/* Has suggestions state */}
      {state === 'hasSuggestions' && (
        <>
          <SparkleIcon className={iconSizeClass} />
          {suggestionCount > 0 && <SuggestionBadge count={suggestionCount} />}
        </>
      )}

      {/* Error state */}
      {state === 'error' && <ErrorIcon className={iconSizeClass} />}
    </button>
  );
};

// Export for external use
export { aiButtonTriggerVariants as buttonTriggerVariants };
